'use client';

import { type FC } from 'react';
import { cn } from '@wwe-portal/ui/lib/lib';
import { GemImage } from './GemImage';
import { Switch } from '@wwe-portal/ui/components/switch';
import { AbilityPerLevel } from '@wwe-portal/entities/superstar-stats.type';
import { Slider } from '@wwe-portal/ui/components/range-slider';
import { TextDiffNumber } from '@wwe-portal/ui/components/text-diff';

export interface UserSuperstarAbility {
  localId: string | undefined;
  equipped: boolean;
  level: number | undefined;
  levelCap: number | undefined;
  movePoint: number | undefined;
  name: string | undefined;
  details: AbilityPerLevel | undefined;
  gemType: string | undefined;
}
export interface RosterAbilityV2Props {
  currentAbility: UserSuperstarAbility;
  baseAbility: UserSuperstarAbility | undefined;
  equipLabel: string;
  onEquipChange: (checked: boolean) => void;
  onLevelChange: (level: number) => void;
}

export const RosterAbilityV2: FC<RosterAbilityV2Props> = ({
  currentAbility: {
    localId,
    equipped,
    movePoint,
    level,
    levelCap,
    name,
    gemType,
    details,
  },
  baseAbility: comparedData,
  equipLabel,
  onEquipChange,
  onLevelChange,
}) => {
  return (
    <div className="bg-back300 odd:bg-back400" data-ability-id={localId}>
      <div className={cn('mb-2.5 flex rounded-md')}>
        <div>
          <div
            className={cn(
              'flex-first border-back200 flex w-[80px] flex-col items-center justify-center p-5'
            )}
          >
            <GemImage gemKey={gemType ?? ''} className="size-14" />
            <p className="text-text400 text-sm italic">MP {movePoint}</p>
          </div>
        </div>
        <div className="w-full">
          <div className="flex-second w-full p-5 pl-0 italic">
            <p className="text-text400 mb-2 text-sm font-semibold">{name}</p>
            <SwitchEquip
              checked={equipped}
              onEquipChange={onEquipChange}
              equipLabel={equipLabel}
            />
            <div className="text-text600 mt-2 text-xs">
              {equipped && details?.text != null ? (
                <TextDiffNumber
                  oldText={
                    comparedData?.equipped
                      ? (comparedData.details?.text ?? '')
                      : ''
                  }
                  newText={details.text}
                  showBaseDiff={false}
                />
              ) : (
                <p>{details?.text}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={cn('w-full px-5 pb-5')}>
        <div className="flex">
          <Slider
            variant="blue"
            className="flex-1"
            onValueChange={(nextValue) => {
              const value = nextValue[0];
              if (value != null) onLevelChange(value);
            }}
            max={levelCap}
            min={1}
            step={1}
            value={[level ?? 1]}
          />
          <span className="drop-shadow-text ml-2 w-16 flex-shrink-0 flex-grow-0 text-center text-sm font-medium italic text-white">
            <span className="text-yellow800">{level}</span> / {levelCap}
          </span>
        </div>
      </div>
    </div>
  );
};

const SwitchEquip = ({
  checked,
  onEquipChange,
  equipLabel,
}: {
  checked?: boolean;
  equipLabel: string;
  onEquipChange: (checked: boolean) => void;
}) => {
  return (
    <div className="flex items-center space-x-2">
      <Switch id="mode" checked={checked} onCheckedChange={onEquipChange} />
      <label
        htmlFor="mode"
        className={cn('text-blue500 text-xs font-semibold uppercase', {
          'text-text600': !checked,
        })}
      >
        {equipLabel}
      </label>
    </div>
  );
};
